// apps/web/src/components/visitors/VisitorForm.tsx
import { useState, useEffect, FormEvent, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import {
  Visitor,
  CreateVisitorRequest,
  UpdateVisitorRequest,
  SubscriptionType,
  ActiveSubscriptionInfo,
} from '@gym-management/shared';
import PhotoUploader from './PhotoUploader';
import { apiClient } from '../../api/client';

interface VisitorFormProps {
  onSubmit: (data: CreateVisitorRequest | UpdateVisitorRequest) => Promise<void>;
  open: boolean;
  onClose: () => void;
  initialData?: Visitor;
  activeSubscription?: ActiveSubscriptionInfo | null;
  onManageSubscription?: (visitorId: string, currentSubscription: ActiveSubscriptionInfo) => void;
}

export default function VisitorForm({
  onSubmit,
  open,
  onClose,
  initialData,
  activeSubscription,
  onManageSubscription,
}: VisitorFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [photoData, setPhotoData] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'period' | 'visits' | ''>('');
  const [selectedSubscriptionTypeId, setSelectedSubscriptionTypeId] = useState('');
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [typesError, setTypesError] = useState<string | null>(null);

  // Загружаем типы абонементов при открытии формы
  useEffect(() => {
    if (open) {
      loadSubscriptionTypes();
    }
  }, [open]);

  // Инициализация формы
  useEffect(() => {
    if (open) {
      if (initialData) {
        setName(initialData.name);
        setPhone(initialData.phone);
        setPhotoData('');
        // Если есть активная подписка — переключатель не показывается, состояние не используется
        if (!activeSubscription) {
          setSubscriptionEnabled(false);
          setSelectedCategory('');
          setSelectedSubscriptionTypeId('');
        }
      } else {
        setName('');
        setPhone('');
        setPhotoData('');
        setSubscriptionEnabled(false);
        setSelectedCategory('');
        setSelectedSubscriptionTypeId('');
      }
    }
  }, [open, initialData, activeSubscription]);

  const loadSubscriptionTypes = async () => {
    setLoadingTypes(true);
    setTypesError(null);
    try {
      const types = await apiClient.getSubscriptionTypes();
      setSubscriptionTypes(types);
    } catch (error) {
      console.error('Failed to load subscription types:', error);
      setTypesError('Не удалось загрузить типы абонементов');
    } finally {
      setLoadingTypes(false);
    }
  };

  // Фильтрация тарифов по выбранному типу (period/visits)
  const filteredTypes = useMemo(() => {
    if (!selectedCategory) return [];
    return subscriptionTypes.filter((t) => t.type === selectedCategory);
  }, [subscriptionTypes, selectedCategory]);

  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCategory(event.target.value as 'period' | 'visits');
    setSelectedSubscriptionTypeId('');
  };

  const handleSubscriptionToggle = (checked: boolean) => {
    setSubscriptionEnabled(checked);
    if (!checked) {
      setSelectedCategory('');
      setSelectedSubscriptionTypeId('');
    }
  };

  const getSubscriptionTypeLabel = (type: SubscriptionType): string => {
    switch (type.type) {
      case 'period':
        return `${type.name} (${type.count} дней)`;
      case 'visits':
        return `${type.name} (${type.count} посещений)`;
      default:
        return `${type.name} (${type.count})`;
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      if (initialData) {
        // ---------- РЕЖИМ РЕДАКТИРОВАНИЯ ----------
        const updateData: UpdateVisitorRequest = {};

        if (name !== initialData.name) updateData.name = name;
        if (phone !== initialData.phone) updateData.phone = phone;
        if (photoData !== '') updateData.photoData = photoData;

        // Если нет активной подписки, но включён переключатель и выбран тариф — создаём подписку
        if (!activeSubscription && subscriptionEnabled && selectedSubscriptionTypeId) {
          updateData.subscriptionTypeId = selectedSubscriptionTypeId;
        }

        const hasVisitorChanges = Object.keys(updateData).length > 0;
        if (hasVisitorChanges) {
          await onSubmit(updateData);
        } else {
          onClose();
        }
      } else {
        // ---------- РЕЖИМ СОЗДАНИЯ ----------
        const createData: CreateVisitorRequest = {
          name,
          phone,
        };

        if (photoData) createData.photoData = photoData;
        if (subscriptionEnabled && selectedSubscriptionTypeId) {
          createData.subscriptionTypeId = selectedSubscriptionTypeId;
        }

        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Error saving visitor:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (newPhotoData: string) => {
    setPhotoData(newPhotoData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать посетителя' : 'Добавить посетителя'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Фото */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Фотография
              </Typography>
              <PhotoUploader
                currentPhoto={initialData?.photoUrl}
                onPhotoChange={handlePhotoChange}
                disabled={loading}
              />
            </Grid>

            {/* Имя */}
            <Grid item xs={12}>
              <TextField
                label="Имя"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Телефон */}
            <Grid item xs={12}>
              <TextField
                label="Телефон"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* ========== СЕКЦИЯ ПОДПИСКИ ========== */}
            {initialData && activeSubscription ? (
              // ЕСТЬ активная подписка — информация и кнопка управления
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.primary">
                      Текущий абонемент
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FitnessCenterIcon />}
                      onClick={() => {
                        onClose();
                        onManageSubscription?.(initialData.id, activeSubscription);
                      }}
                    >
                      Управление абонементом
                    </Button>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {activeSubscription.subscriptionTypeName}
                  </Typography>
                  {activeSubscription.endDate && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Действует до: {new Date(activeSubscription.endDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {activeSubscription.subscriptionTypeRemaining !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {activeSubscription.subscriptionTypeType === 'period'
                        ? `Осталось дней: ${activeSubscription.subscriptionTypeRemaining}`
                        : `Осталось посещений: ${activeSubscription.subscriptionTypeRemaining}`}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ) : (
              // НЕТ активной подписки — двухэтапный выбор абонемента
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={subscriptionEnabled}
                        onChange={(e) => handleSubscriptionToggle(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Абонемент"
                  />
                </Grid>

                {subscriptionEnabled && (
                  <>
                    {/* ШАГ 1: Выбор типа абонемента */}
                    <Grid item xs={12}>
                      <FormControl component="fieldset" disabled={loading || loadingTypes}>
                        <FormLabel component="legend">Тип абонемента</FormLabel>
                        <RadioGroup row value={selectedCategory} onChange={handleCategoryChange}>
                          <FormControlLabel
                            value="period"
                            control={<Radio />}
                            label="Срочный (дни)"
                          />
                          <FormControlLabel
                            value="visits"
                            control={<Radio />}
                            label="По посещениям"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* ШАГ 2: Выбор конкретного тарифа */}
                    {selectedCategory && (
                      <Grid item xs={12}>
                        <FormControl fullWidth disabled={loading || loadingTypes}>
                          <InputLabel id="subscription-type-label">Тариф</InputLabel>
                          <Select
                            labelId="subscription-type-label"
                            value={selectedSubscriptionTypeId}
                            label="Тариф"
                            onChange={(e) => setSelectedSubscriptionTypeId(e.target.value)}
                            required
                          >
                            {filteredTypes.length > 0 ? (
                              filteredTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {getSubscriptionTypeLabel(type)}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled value="">
                                Нет доступных тарифов
                              </MenuItem>
                            )}
                          </Select>
                          {loadingTypes && <CircularProgress size={20} sx={{ mt: 1 }} />}
                          {typesError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {typesError}
                            </Alert>
                          )}
                          {!loadingTypes && filteredTypes.length === 0 && selectedCategory && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              Для выбранного типа абонемента пока нет активных тарифов.
                            </Alert>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              !name.trim() ||
              !phone.trim() ||
              // В режиме создания или редактирования без подписки — проверяем выбор типа, если переключатель включён
              (!initialData &&
                subscriptionEnabled &&
                (!selectedCategory || !selectedSubscriptionTypeId)) ||
              // В режиме редактирования с подпиской — поле подписки не участвует в валидации
              (initialData &&
                !activeSubscription &&
                subscriptionEnabled &&
                (!selectedCategory || !selectedSubscriptionTypeId))
            }
          >
            {loading ? <CircularProgress size={24} /> : initialData ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
